import {
  NookApiError,
  PlanExpiredError,
  useDeleteNook,
  useNook,
  useRegenerateSummary,
  type Nook,
} from "@nook/api-client"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Textarea,
  toast,
  ToggleGroup,
  ToggleGroupItem,
} from "@nook/ui"
import {
  formatClock,
  formatFullDate,
  Markdown,
  nookHost,
  nookTitle,
  readingMinutes,
  SourceIcon,
  transcriptOf,
  transcriptTime,
  withoutLeadImages,
  withoutTitle,
} from "@nook/client-ui"
import {
  AArrowDown,
  AArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileX,
  Link2,
  Loader2,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
  Type,
} from "lucide-react"
import { readsAsArticle } from "@nook/schema"
import { useRef, useState } from "react"

import { PlatformEmbed } from "@/components/embeds/platform-embed.js"
import { EmptyState, GhostCards } from "@/components/empty-state.js"
import { useKeys } from "@/components/keys.js"
import { isNote, NoteDocument, useNoteAutosave } from "@/components/note-editor.js"
import { UpgradePrompt } from "@/components/upgrade-prompt.js"
import { READER_SIZES, useReader } from "@/lib/reader.js"

/** An article's own text; a platform pipeline's content is a transcript or post, shown its own way. */
const articleOf = (nook: Nook) =>
  nook.sourceUrl && !nook.type && readsAsArticle(nook.platform) && nook.status === "ready"
    ? nook.content
    : null

/**
 * The open nook: beside the library on `/`, in a
 * dialog over an Ask answer. `ids` is the list it was opened from — the one
 * `J`/`K` and the arrow buttons step through. The caller owns where the open id
 * lives (`/?nook=` or Ask's own state), so this only reports moves.
 * `expandable` offers Instapaper's expand, where the host sizes the panel by `useReader().wide`.
 */
export function NookPanel({
  id,
  ids = [],
  expandable = false,
  onOpen,
  onClose,
}: {
  id: string
  ids?: string[]
  expandable?: boolean
  onOpen: (id: string) => void
  onClose: () => void
}) {
  const nook = useNook(id)
  const [reader, setReader] = useReader()
  // The id the summary popover is open for, so stepping to another nook closes it.
  const [summaryFor, setSummaryFor] = useState<string | null>(null)
  const progress = useRef<HTMLDivElement>(null)
  const article = nook.data && articleOf(nook.data)
  const index = ids.indexOf(id)
  const prev = index > 0 ? ids[index - 1] : undefined
  const next = index >= 0 ? ids[index + 1] : undefined

  // Set once per id, so a later render (the nook loading) cannot restart the slide.
  const seen = useRef({ id, index, slide: "" })
  if (seen.current.id !== id)
    seen.current = {
      id,
      index,
      slide:
        index < seen.current.index ? "slide-in-from-left-3" : "slide-in-from-right-3",
    }

  // Subscribed only to warm the cache, so a step lands on a loaded nook.
  useNook(prev ?? id)
  useNook(next ?? id)

  // A nook belonging to someone else answers `404`, exactly like one that never
  // existed — the backend must not let a caller tell those apart, and neither
  // does this copy.
  const missing = nook.error instanceof NookApiError && nook.error.status === 404
  const source = nook.data?.sourceUrl

  const step = (to: string | undefined) => () => to && onOpen(to)
  const resize = (by: number) => () =>
    article && setReader({ size: Math.min(Math.max(reader.size + by, 0), READER_SIZES.length - 1) })
  useKeys({
    j: step(next),
    ArrowRight: step(next),
    k: step(prev),
    ArrowLeft: step(prev),
    o: () => source && window.open(source, "_blank", "noopener"),
    w: () => expandable && setReader({ wide: !reader.wide }),
    "=": resize(1),
    "Shift+Equal": resize(1),
    "-": resize(-1),
    Escape: onClose,
  })

  return (
    <article className="bg-card flex h-full min-w-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-1.5 border-b px-5">
        <Button
          variant="outline"
          size="sm"
          className="h-7.5 gap-1.5 px-2"
          aria-label="Previous nook"
          disabled={!prev}
          onClick={step(prev)}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7.5 gap-1.5 px-2"
          aria-label="Next nook"
          disabled={!next}
          onClick={step(next)}
        >
          <ChevronRight />
        </Button>

        <span className="flex-1" />

        {nook.data && !isNote(nook.data) ? (
          <SummaryMenu
            nook={nook.data}
            open={summaryFor === id}
            onOpenChange={(open) => setSummaryFor(open ? id : null)}
          />
        ) : null}
        {article ? <ReaderMenu /> : null}
        {expandable ? (
          <Button
            variant="outline"
            size="icon-sm"
            className="size-7.5 max-md:hidden"
            aria-label={reader.wide ? "Shrink" : "Expand"}
            onClick={() => setReader({ wide: !reader.wide })}
          >
            {reader.wide ? <Minimize2 /> : <Maximize2 />}
          </Button>
        ) : null}
        {source ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7.5 gap-2 pr-1.5 pl-2.5 text-[0.8125rem] font-normal max-sm:px-2"
            asChild
          >
            <a
              href={source}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Open original"
            >
              <ExternalLink />
              <span className="max-sm:hidden">Open original</span>
            </a>
          </Button>
        ) : null}
        {nook.data ? <NookMenu nook={nook.data} onDeleted={onClose} /> : null}
        <Button
          variant="ghost"
          size="sm"
          className="h-7.5 px-2 text-[0.8125rem] font-normal"
          onClick={onClose}
        >
          Close
        </Button>
      </header>

      {/* How far through an article, as on iOS; nothing is saved. Keyed so a step starts it empty. */}
      <div className="relative h-0 shrink-0">
        {article ? (
          <div
            key={id}
            ref={progress}
            className="bg-foreground absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0"
          />
        ) : null}
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto"
        onScroll={({ currentTarget: { scrollTop, scrollHeight, clientHeight } }) => {
          setSummaryFor(null)
          progress.current?.style.setProperty(
            "scale",
            `${Math.min(1, scrollTop / Math.max(1, scrollHeight - clientHeight))} 1`,
          )
        }}
      >
        {/* Keyed so each nook starts at its top. */}
        <div
          key={id}
          className={`animate-in fade-in-0 ${seen.current.slide} mx-auto flex max-w-[760px] flex-col gap-7 px-5 py-6 md:px-16 md:py-10 duration-150 ease-out`}
        >
          {nook.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : missing ? (
            <EmptyState
              art={<GhostCards icons={[FileX]} />}
              title="This nook isn't in your library"
              description="It may have been deleted, or the link is wrong."
              action={<Button onClick={onClose}>Back to Library</Button>}
            />
          ) : nook.isError ? (
            <p className="text-destructive text-sm">
              Could not load this nook. Try reloading.
            </p>
          ) : isNote(nook.data) ? (
            <NoteDocument key={nook.data.id} nook={nook.data} />
          ) : (
            <>
              <NookDetail nook={nook.data} />
              {/* Keyed: a draft that followed the user to the next nook would save onto it. */}
              <NoteEditor key={nook.data.id} nook={nook.data} />
            </>
          )}
        </div>
      </div>
    </article>
  )
}

/** Instapaper's Aa: the article's face and size, kept for every article. */
function ReaderMenu() {
  const [reader, setReader] = useReader()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon-sm" className="size-7.5" aria-label="Text settings">
          <Type />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-56 flex-col gap-2 p-2">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          className="w-full"
          value={reader.sans ? "sans" : "serif"}
          onValueChange={(value) => value && setReader({ sans: value === "sans" })}
        >
          <ToggleGroupItem value="serif" className="font-reader flex-1">
            Serif
          </ToggleGroupItem>
          <ToggleGroupItem value="sans" className="flex-1">
            Sans
          </ToggleGroupItem>
        </ToggleGroup>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Smaller text"
            disabled={reader.size === 0}
            onClick={() => setReader({ size: reader.size - 1 })}
          >
            <AArrowDown />
          </Button>
          <span className="text-muted-foreground flex-1 text-center text-[0.8125rem] tabular-nums">
            {READER_SIZES[reader.size]}px
          </span>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Larger text"
            disabled={reader.size === READER_SIZES.length - 1}
            onClick={() => setReader({ size: reader.size + 1 })}
          >
            <AArrowUp />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Delete has no undo, because there is no restore: block 04 ships hard delete
 * and trash is frozen. An irreversible action gets a confirmation instead.
 * The row leaves the list optimistically, so `onDone(true)` comes straight
 * away, not on success; a failure rolls the list back and says so.
 */
export function ConfirmDelete({
  id,
  onDone,
}: {
  id: string | null
  onDone: (deleted: boolean) => void
}) {
  const remove = useDeleteNook()

  return (
    <AlertDialog open={id !== null} onOpenChange={(open) => !open && onDone(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this nook?</AlertDialogTitle>
          <AlertDialogDescription>
            It is removed from your library permanently. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              remove.mutate(id!, {
                onSuccess: () => toast.success("Deleted"),
                onError: (error) => toast.error(error.message),
              })
              onDone(true)
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** The header's `⋯`: everything a nook can have done *to* it. Regenerate lives in the summary popover. */
function NookMenu({ nook, onDeleted }: { nook: Nook; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState<string | null>(null)

  useKeys({ "$mod+Backspace": () => setConfirming(nook.id) })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            className="size-7.5"
            aria-label="More"
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          {nook.sourceUrl ? (
            <DropdownMenuItem
              onSelect={() =>
                void navigator.clipboard.writeText(nook.sourceUrl!).then(
                  () => toast.success("Link copied"),
                  () => toast.error("Couldn't copy the link"),
                )
              }
            >
              <Link2 />
              Copy link
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => setConfirming(nook.id)}
          >
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDelete
        id={confirming}
        onDone={(deleted) => {
          setConfirming(null)
          if (deleted) onDeleted()
        }}
      />
    </>
  )
}

function Section({
  title,
  aside,
  children,
}: {
  title: React.ReactNode
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2.5 border-t pt-6">
      <div className="flex min-h-7.5 items-center justify-between gap-2">
        <h2 className="text-[0.8125rem] font-medium">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

function NookDetail({ nook }: { nook: Nook }) {
  const [reader] = useReader()
  const host = nookHost(nook)
  // A note has no title of its own — its heading is its first line — so the
  // note is the body, and the heading only exists for the outline.
  const isNote = !nook.sourceUrl
  // The post as its platform draws it (block 43). It carries its own author and
  // text, so the thumbnail and most previews give way to it.
  const embed = nook.media?.embed
  const noteRest = isNote
    ? nook.content?.trim().split("\n").slice(1).join("\n").trim()
    : null
  const article = articleOf(nook)
  const transcript = transcriptOf(nook)
  // An image is the picture alone: no title, meta or read text.
  const image = !!nook.storagePath && !nook.sourceUrl

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className={cn("text-muted-foreground flex items-center gap-2 text-xs", image && "hidden")}>
          <SourceIcon icon={nook.sourceIcon} />
          <span>{host ?? "Note"}</span>
          {nook.type ? (
            <>
              <span aria-hidden>·</span>
              <span className="capitalize">{nook.type}</span>
            </>
          ) : null}
          {article ? (
            <>
              <span aria-hidden>·</span>
              <span>{readingMinutes(article)} min read</span>
            </>
          ) : null}
          <span aria-hidden>·</span>
          <span>Nooked {formatFullDate(nook.capturedAt)}</span>
        </div>

        {/* An embed draws its own header; the heading stays for the outline. */}
        <h2
          className={cn(
            "text-[1.625rem] leading-[1.15] font-semibold tracking-tight text-pretty md:text-[2rem]",
            article && !reader.sans && "font-reader tracking-normal",
            (embed || image) && "sr-only",
          )}
        >
          {nookTitle(nook)}
        </h2>

        {(nook.media?.resumeAt && !embed) || nook.media?.transcriptUnavailable ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* A player already starts there. */}
            {nook.media?.resumeAt && nook.sourceUrl && !embed ? (
              <Button variant="outline" size="sm" asChild>
                <a href={nook.sourceUrl} target="_blank" rel="noreferrer noopener">
                  <Play />
                  Resume at {formatClock(nook.media.resumeAt)}
                </a>
              </Button>
            ) : null}
            {nook.media?.transcriptUnavailable ? (
              <Badge variant="secondary">Transcript unavailable</Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Keyed so a frame's measured height does not follow the pane to the next nook. */}
      {embed ? <PlatformEmbed key={nook.id} embed={embed} /> : null}

      {/*
        Signed for an hour and re-signed by every refetch, so a pane left open
        past the expiry recovers on its own (plan §3.6). `imageUrl` is null
        until `moderate-image` has cleared the nook.
      */}
      {nook.imageUrl ? (
        <img
          src={nook.imageUrl}
          alt={nook.userNote ?? "Nooked image"}
          className="bg-muted w-full rounded-[10px]"
        />
      ) : nook.storagePath && nook.status === "processing" ? (
        <div className="bg-muted text-muted-foreground flex items-center justify-center gap-2 rounded-[10px] py-12 text-sm">
          <Loader2 className="size-4 animate-spin" />
          Checking this image…
        </div>
      ) : nook.storagePath && nook.status === "failed" ? (
        <p className="text-muted-foreground bg-muted rounded-[10px] py-12 text-center text-sm">
          This image did not finish uploading.
        </p>
      ) : nook.thumbnailUrl && !embed && !article ? (
        // The page's own image: our re-hosted copy, or the publisher's URL until then (A11). An article opens with its words instead.
        <img
          src={nook.thumbnailUrl}
          alt=""
          className="bg-muted w-full rounded-[10px]"
        />
      ) : null}

      {image ? null : nook.highlightColor ? (
        <HighlightQuote nook={nook} />
      ) : isNote ? (
        // The heading is the note's first line, so the body starts after it.
        noteRest ? (
          <p className="text-[0.9375rem] leading-relaxed whitespace-pre-wrap">
            {noteRest}
          </p>
        ) : null
      ) : nook.content && embed?.kind === "x" && embed.tweet.note_tweet ? (
        // A long post's snapshot stops at 280 characters; `content` is the rest.
        <div className="rounded-[10px] border p-4 text-sm leading-relaxed whitespace-pre-wrap">
          {nook.content}
        </div>
      ) : nook.status === "processing" ? (
        <section className="space-y-2" aria-busy>
          <Badge variant="secondary">
            <Loader2 className="animate-spin" />
            Processing…
          </Badge>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </section>
      ) : article ? (
        <ArticleBody markdown={withoutTitle(withoutLeadImages(article), nook.sourceTitle)} />
      ) : transcript ? (
        <Transcript lines={transcript} />
      ) : nook.extractionError ? (
        // The nook stays, and so does Open original (PRD §13).
        <p className="text-muted-foreground text-sm">
          We couldn&apos;t extract this page. Open the original above — it is still
          nooked here either way.
        </p>
      ) : null}
    </>
  )
}

/** A video's transcript, folded shut: one `[00:56]` line per stretch of about 30 seconds. */
function Transcript({ lines }: { lines: NonNullable<ReturnType<typeof transcriptOf>> }) {
  return (
    <Collapsible className="rounded-[10px] border">
      <CollapsibleTrigger className="group flex w-full items-center justify-between px-4 py-3 text-sm font-medium">
        Transcript
        <ChevronDown className="text-muted-foreground size-4 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3 border-t px-4 py-3 text-[0.9375rem] leading-relaxed">
        {lines.map(({ start, text }, index) => (
          <p key={index}>
            {start !== null ? (
              <span className="text-muted-foreground mr-2 font-mono text-[0.8125rem] tabular-nums">
                [{transcriptTime(start)}]
              </span>
            ) : null}
            {text}
          </p>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

/** The extracted article, whole, in the reader's face and size (Instapaper, 2026-10-30). */
function ArticleBody({ markdown }: { markdown: string }) {
  const [reader] = useReader()

  return (
    <div style={{ "--reader-size": `${READER_SIZES[reader.size]}px` } as React.CSSProperties}>
      <Markdown className={cn("reader [&_img]:rounded-[10px]", !reader.sans && "font-reader")}>
        {markdown}
      </Markdown>
    </div>
  )
}

/**
 * The header's Summary, written only on request (block 14): opening a nook spends nothing. The first
 * click — or `S` — enqueues it and opens the popover over the page, as on iOS; later ones open and
 * close it, and a click outside or a scroll of the nook closes it. A regeneration keeps the old words
 * in place, dimmed, so nothing moves until the new ones land.
 */
function SummaryMenu({
  nook,
  open,
  onOpenChange,
}: {
  nook: Nook
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const regenerate = useRegenerateSummary()
  const [expired, setExpired] = useState(false)
  const busy = nook.summaryPending || regenerate.isPending

  const run = () =>
    regenerate.mutate(nook.id, {
      onError: (error) => {
        onOpenChange(false)
        if (error instanceof PlanExpiredError) setExpired(true)
        else toast.error(error.message)
      },
    })
  const toggle = () => {
    const generate = !nook.summary && !busy
    if (generate) run()
    onOpenChange(generate || !open)
  }

  const offered = nook.summaryPending || !!nook.summary || nook.summarizable
  useKeys({ s: toggle }, offered)

  if (!offered) return null

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            className="size-7.5"
            aria-label={nook.summary || busy ? "Summary" : "Generate summary"}
            // Radix's own toggle is skipped: the first click also writes the summary.
            onClick={(event) => {
              event.preventDefault()
              toggle()
            }}
          >
            <Sparkles />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          aria-label="Summary"
          className="flex max-h-[min(32rem,70vh)] w-[min(28rem,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto px-5 pt-3 pb-5"
        >
          <div className="flex min-h-8 items-center justify-between">
            <h2 className="text-muted-foreground text-[0.8125rem] font-medium">Summary</h2>
            {nook.summary ? (
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground -mr-2"
                aria-label="Regenerate summary"
                disabled={busy}
                onClick={run}
              >
                {busy ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              </Button>
            ) : null}
          </div>
          {nook.summary ? (
            <p
              aria-busy={busy}
              className={cn("text-[0.9375rem] leading-6 text-pretty", busy && "opacity-40")}
            >
              {nook.summary}
            </p>
          ) : (
            // About as long as a summary runs, a bar per line at the text's own leading.
            <div aria-busy>
              {[100, 97, 100, 94, 100, 52].map((width, index) => (
                <div key={index} className="flex h-6 items-center">
                  <Skeleton className="h-2.5 rounded-full" style={{ width: `${width}%` }} />
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
      <UpgradePrompt open={expired} onOpenChange={setExpired} />
    </>
  )
}

/** A highlight is the passage, in its color, and the color is editable here too (block 45). */
function HighlightQuote({ nook }: { nook: Nook }) {
  // const update = useUpdateNook()
  // const colors = highlightColorSchema.options
  // const paint = (color: (typeof colors)[number]) =>
  //   update.mutate({ id: nook.id, highlightColor: color }, { onError: (error) => toast.error(error.message) })

  // useKeys(Object.fromEntries(colors.map((color, index) => [String(index + 1), () => paint(color)])))

  return (
    <>
      <blockquote
        className="bg-muted m-0 rounded-[10px] px-6 py-5 text-[1.0625rem] leading-relaxed whitespace-pre-wrap"
        style={{ borderLeft: `3px solid ${nook.highlightColor ? `var(--highlight-${nook.highlightColor})` : "transparent"}` }}
      >
        {nook.content}
      </blockquote>
      {/* <div className="flex items-center gap-3 text-[0.8125rem]">
        <span className="text-muted-foreground">Highlight colour</span>
        <div role="radiogroup" aria-label="Highlight color" className="flex gap-2">
          {colors.map((color) => (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={color === nook.highlightColor}
              aria-label={color}
              title={`${color[0]!.toUpperCase()}${color.slice(1)} (${colors.indexOf(color) + 1})`}
              className="aria-checked:ring-foreground ring-offset-background size-7 rounded-full border transition-transform hover:scale-110 active:scale-95 aria-checked:ring-2 aria-checked:ring-offset-2"
              style={{ backgroundColor: `var(--highlight-${color})` }}
              onClick={() => paint(color)}
            />
          ))}
        </div>
      </div> */}
    </>
  )
}

/** The user's own note on a nook (block 37). Plain text: it predates markdown notes, and a rich editor would rewrite what is stored. `E` puts the cursor in it. */
function NoteEditor({ nook }: { nook: Nook }) {
  const note = useNoteAutosave(nook)
  const field = useRef<HTMLTextAreaElement>(null)

  useKeys({ e: () => field.current?.focus() })

  return (
    <Section
      title={
        <label htmlFor="note" className="flex items-center gap-2">
          Notes
          <span
            aria-live="polite"
            className={`text-xs font-normal ${note.failed ? "text-destructive" : "text-muted-foreground"}`}
          >
            {note.status}
          </span>
        </label>
      }
    >
      <Textarea
        id="note"
        ref={field}
        placeholder="Add a note…"
        value={note.value}
        onChange={(event) => note.setValue(event.target.value)}
        onBlur={note.save}
        onKeyDown={(event) => event.key === "Escape" && event.currentTarget.blur()}
        className="bg-background h-24 resize-none text-sm"
      />
    </Section>
  )
}
